import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "../../components/button/Button";
import Badge from "../../components/badges/Badges";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { SelectFields, SelectItem } from "../../components/select/SelectFields";
import { useInput } from "../../hooks/useInput";
import FormController from "../../components/fields/FormController";
import { Spiner } from "../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { ExecutionModal } from "./ExecutionModal";
import { ExecutionCompleteModal } from "./ExecutionCompleteModal";
import { TargetUserSelectedModal } from "./TargetUserSelectedModal";
import pointDataQuery from '../../firestore/PointData.query';
import { auth } from "../../firebase";
import { 
  PointAdjustHistory, 
  PointCategory, 
  PointType, 
  newPointAdjustHistory, 
  pointAdjustSchema,
  PtAwardedSettings,
} from "../../model/PointData";
import { Progress } from "../../components/progress/Progress";
import { Company } from "../../model/Companies";
import companiesQuery from "../../firestore/Companies.query";
import { UserData } from "../../model/Users";
import { yupResolver } from "@hookform/resolvers/yup";
import { SearchUserList, UserListSearchDefault } from "../../model/Users";
import { CheckBox } from "../../components/checkBox/CheckBox";
import { TextField } from "../../components/fields/Fields";
import { checkOption } from "../../controller/OptionController";
import { requestGetUser } from "../../api/Users.api";

export const PointAdjustmentPage = () => {

  const [isLoad, setIsLoad] = useState(true);
  const [isOpenTargetUserSelect, setIsOpenTargetUserSelect] = useState(false);
  const [isVoid, setIsVoid] = useState(false);
  const [selectGroup, setSelectGroup] = useState<{id: string, title: string}[]>([]);
  const [groupsValue, setGroupsValue] = useState<CheckBoxItem[]>([]);
  const [defaultGroups, setDefaultGroups] = useState<CheckBoxItem[]>([]);
  const [isExecutionModalOpen, setIsExecutionModalOpen] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [company, setCompany] = useState<Company>();
  const [userList, setUserList] = useState<UserData[]>([]);
  const [pointCategoryList, setPointCategoryList] = useState<PointCategory[]>();
  const [pointTypeList, setPointTypeList] = useState<PointType[]>();
  const [selectCategoryList, setSelectCategoryList] = useState<RadioButtonItem[]>([]);
  const [selectTypeList, setSelectTypeList] = useState<SelectItem[]>([]);
  const [inputData, setInputData] = useState<PointAdjustHistory>(newPointAdjustHistory);
  const [isExecutionCompleteDialogOpen, setIsExecutionCompleteDialogOpen] = useState(false);
  const [isExecution, setIsExecution] = useState(false);
  const [submitErrFlg, setSubmitErrFlg] = useState(false);
  const [userListResult, setUserListResult] = useState<SearchUserList>(UserListSearchDefault);
  const [userFirstPageFlg, setUserFirstPageFlg] = useState(false);
  const [checkedAllValue, setCheckedAllValue] = useState<boolean>(false);
  const [userEditRows, setUserEditRows] = useState<GridRow[]>([]);
  const [targetUserRows, setTargetUserRows] = useState<GridRow[]>([]);
  const [searchUserListFlg, setSearchUserListFlg] = useState(false);
  const [searchUserList, setSearchUserList] = useState<UserData[]>([]);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [executionErrFlg, setExecutionErrFlg] = useState<boolean>(false);
  const [ptAwardedSettingsList, setPtAwardedSettingsList] = useState<PtAwardedSettings[]>([]);
  const [oldCheckedList, setOldCheckedList] = useState<string[]>([]);
  const [oldCheckedUserMap, setOldCheckedUserMap] = useState<Record<string, UserData>>({});
  const [openTargetUserFlg, setOpenTargetUserFlg] = useState<boolean>(false);
  const [checkedUserRow, setCheckedUserRow] = useState<GridRow[]>([]);
  const [searchCondition, setSearchCondition] = useState<any>({}); 
  const [nowUserPage, setNowUserPage] = useState(1);
  const [selectUserNumber, setSelectUserNumber] = useState(10);
  const [totalUserCount, setTotalUserCount] = useState<number>(0);
  const [isUserListLoading, setIsUserListLoading] = useState(false);
  const [firstUserPageFlg, setFirstUserPageFlg] = useState(true);
  const [checkedUserMap, setCheckedUserMap] = useState<Record<string, UserData>>({});

  const { control, setValue, handleSubmit, getValues, reset } = 
    useForm<PointAdjustHistory & {ptTypeFlg: boolean}>(
      { resolver: yupResolver(pointAdjustSchema), defaultValues: newPointAdjustHistory()}
    );
  const selectButton = useInput("0");
  const dispatch = useDispatch();

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  const currentList = searchUserListFlg ? searchUserList : userList;
  const allCheckedOnPage =
    currentList.length > 0 &&
    currentList.every((user) => checkedList.includes(user.id));

  useEffect(() => {
    const f = async () => {
      try {
        setIsLoad(true);
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);
        // 団体グループ取得
        const groupList = (await companiesQuery.getGroups(company.id)).filter((group) => group.deleteFlg === false);
        // グループチェックボックスの一覧データ
        const defaultGroupsValue = await Promise.all(groupList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.name,
            value: false,
            disabled: item.deleteFlg,
          }
        }));
        setGroupsValue(defaultGroupsValue);
        setDefaultGroups(defaultGroupsValue);
        // ptAwardedSettings取得
        const ptAwardedSettingsList = await pointDataQuery.getPtAwardedSettings(currentUser?.tenantId!);
        setPtAwardedSettingsList(ptAwardedSettingsList);
        // ポイントカテゴリを取得
        const ptCategoryList = await pointDataQuery.getPointCategoryList();
        setPointCategoryList(ptCategoryList);
        // ラジオボタン用のポイントカテゴリデータ
        const selectCategoriesItem = ptCategoryList.map((item) => ({id: String(item.categoryId), title: item.categoryName}));
        setSelectCategoryList(selectCategoriesItem);
        // ポイントタイプを取得
        const ptTypeList = await pointDataQuery.getPointTypeList();
        setPointTypeList(ptTypeList);
        // ボランティアポイントのポイントタイプをセレクトボックスの値に設定する
        const selectType = ptTypeList.filter((item) => item.categoryId === 0);
        let selectTypeItem = [{id: "", name: ""}];
        selectType.map((item) => (selectTypeItem.push({id: item.typeId, name: item.typeName})));
        setSelectTypeList(selectTypeItem);
      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      } finally {
        setIsLoad(false);
      }
    };
    f();
  },[]);

  // 対象者取得
  useEffect(() => {
    if (!isOpenTargetUserSelect) return;
    const fetchUsersByPage = async () => {
      const mergedSearchConditions = {
        ...searchCondition,
        accountSuspensionFlg: false,
      };
      const result = await requestGetUser(nowUserPage, selectUserNumber, mergedSearchConditions);
      if(!result.success) throw result.error;
      const users = result.users as UserData[];
      setUserList(users);
      const checkedSet = new Set(checkedList);
      const userRows: GridRow[] = users.map(user => {
        const userGroupList = user.groups?.length
          ? user.groups.map(g => (
              <Badge key={g.id} color="blue">{g.name}</Badge>
            ))
          : null;

        return {
          rowData: {
            allChecked: (
              <CheckBox
                id={user.id}
                class="checks"
                onChange={() => checkedListAdd(user.id)}
                checked={checkedSet.has(user.id)}
              />
            ),
            userId: user.displayId,
            name: user.lastName,
            group: userGroupList,
          },
          style: 'sm:whitespace-normal'
        };
      });

      setUserEditRows(userRows);
      setUserFirstPageFlg(nowUserPage === 1);
      setTotalUserCount(result.totalCount);

      setIsUserListLoading(false);
    };
    fetchUsersByPage();
  }, [nowUserPage, selectUserNumber, searchCondition, isOpenTargetUserSelect]);

  // ユーザー編集　検索ボタン押下
  const handleSearch = (condition: any) => {
    setIsUserListLoading(true);
    setFirstUserPageFlg(true);
    setSearchCondition(condition);
    setNowUserPage(1);
  };

  // ユーザー編集 ページング処理
  const pagingUserFunc = useCallback((page: number) => {
    setFirstUserPageFlg(false);
    setNowUserPage(page);
  }, []);

  useEffect(() => {
    setFirstUserPageFlg(true)
  }, [searchUserListFlg, searchUserList])

  useEffect(() => {
    const currentListToUse = searchUserListFlg ? searchUserList : userList;
    if (!currentListToUse) return;

    const checkedSet = new Set(checkedList);

    const rows: GridRow[] = currentListToUse.map(user => {
      const userGroupList = user.groups?.length
        ? user.groups.map(g => <Badge key={g.id} color="blue">{g.name}</Badge>)
        : null;

      return {
        rowData: {
          allChecked: (
            <CheckBox
              id={user.id}
              class="checks"
              onChange={() => checkedListAdd(user.id)}
              checked={checkedSet.has(user.id)}
            />
          ),
          userId: user.displayId,
          name: user.lastName,
          group: userGroupList,
        },
        style: 'sm:whitespace-normal',
      };
    });

    setUserEditRows(rows);
  }, [checkedList, userList, searchUserList, searchUserListFlg]);

  // 対象者モーダル開閉時
  useEffect(() => {
    const f = async () => {
      if(userList){
        const checkedSet = new Set(checkedList);
        const userRows: GridRow[] = (searchUserListFlg ? searchUserList : userList).map<GridRow>((item) => {
          const userGroupList = item.groups?.length? item.groups.map((group: any) => (
            <Badge key={group.id} color="blue">
              {group.name}
            </Badge>
            )) : null;
          return {
            rowData: {
              allChecked: <CheckBox
                            id={item.id}
                            class={"checks"}
                            onChange={() => checkedListAdd(item.id)}
                            checked={checkedSet.has(item.id)}
                          />,
              userId: item.displayId,
              name: item.lastName,
              group: userGroupList, 
            },
            style: 'sm:whitespace-normal'
          }
        });
        setUserEditRows(userRows);
      }
    };
    f();
  },[openTargetUserFlg, searchUserList, checkedList]);

  // ラジオボタンを押下した時のポイントタイプを設定
  useEffect(() => {
    try{
      const selectType = pointTypeList?.filter((item) => item.categoryId === Number(selectButton.value));
      let selectTypeItem = [{id: "", name: ""}];
      selectType?.map((item) => (selectTypeItem.push({id: item.typeId, name: item.typeName})));
      setSelectTypeList(selectTypeItem);
      setValue('ptCategoryId', selectButton.value);
      setValue('ptTypeId', selectTypeItem[0].id);
      setInputData({...inputData, ptTypeId: selectTypeItem[0].id});
    }catch(err){
      console.error(err);
    }
  },[selectButton.value]);

  // インポート完了ダイアログ用プロパティ
  useEffect(() => {
    if(loadProgress >= 100 && isExecution) {
      setIsExecutionCompleteDialogOpen(!isExecutionCompleteDialogOpen);
      setIsExecution(false);
      // 検索項目、調整設定項目を初期化
      setCheckedList([]);
      // チェックユーザーマップと表示用行もクリアする
      setCheckedUserMap({});
      setCheckedUserRow([]);
      setTargetUserRows([]);
      setOldCheckedList([]);
      setCheckedAllValue(false);
      setSearchUserListFlg(false);
      setSearchUserList([]);
      setSelectGroup([]);
      setGroupsValue(defaultGroups);
      setUserListResult(UserListSearchDefault);
      selectButton.setValue('0');
      reset();
      setValue("grantPoints", 0);
      setInputData({...inputData, grantPoints: 0, ptTypeId: ""});
      setLoadProgress(-1);
    }
  },[loadProgress, isExecution]);

  // 対象者検索を実行時、利用者一覧は1ページ目を表示する
  useEffect(() => {
    setUserFirstPageFlg(true);
    // チェックを初期化
    setCheckedList([]);
    setCheckedAllValue(false);
  }, [searchUserListFlg, searchUserList])

  // 対象者選択モーダルの開閉
  const toggle = async () => {
    if(!isOpenTargetUserSelect){
      // モーダルを開く前のチェック状態を保存
      setOldCheckedList(checkedList);
      setOldCheckedUserMap(checkedUserMap);
      setOpenTargetUserFlg(!openTargetUserFlg);
      setIsUserListLoading(true);
    }
    setIsOpenTargetUserSelect(!isOpenTargetUserSelect);
    setIsVoid(!isVoid);
  };

  // 対象者選択をキャンセル
  const onSelectCancel = () => {
    setIsOpenTargetUserSelect(!isOpenTargetUserSelect);
    setCheckedList(oldCheckedList);
    setCheckedUserMap(oldCheckedUserMap);
    setIsVoid(!isVoid);
  };

  const categoryName = [
    {id: 0, name: "ボランティア"},
    {id: 1, name: "その他"},
    {id: 2, name: "アプリ"}
  ];

  // 対象者を選択
  const onClickSelect = async (data: any) => {
    setIsOpenTargetUserSelect(!isOpenTargetUserSelect);
    if(userList){
      const userMap = new Map(userList.map(user => [user.id, user]));
      setOldCheckedList(checkedList);
      const checkedUserList = checkedList
        .map(id => userMap.get(id))
        .filter((u): u is UserData => !!u); // null・undefined 除外

      if(checkedUserList.length > 0){
        setSubmitErrFlg(false);
        checkedUserList.sort((a, b) => { 
          if(a.displayId && b.displayId){
            if (a.displayId > b.displayId) return 1;
            if (a.displayId < b.displayId) return -1;
          }
          return 0;
        });
      }
      const groupUserRows: GridRow[] = checkedUserList.map(item => {
        const userGroupList = item.groups?.length
          ? item.groups.map(group => (
              <Badge key={group.id} color="blue">
                {group.name}
              </Badge>
            ))
          : null;

        const points = (
          <div className="w-[10rem] whitespace-break-spaces">
            {item.possessedPointMap?.length
              ? item.possessedPointMap.map(p => (
                  <div key={p.id} className="flex items-center">
                    <div className="w-[7rem] text-right">{p.id ? categoryName[Number(p.id)].name : ""}：</div>
                    <div className="w-[3rem] text-right">{p.point}</div>
                  </div>
                ))
              : null}
          </div>
        );

        return {
          rowData: {
            userId: item.displayId,
            name: item.lastName,
            point: points,
            group: userGroupList,
          },
          style: 'sm:whitespace-normal',
        };
      });
      setTargetUserRows(groupUserRows);
    };
  };

  // 対象者選択チェックボックス(一括)
  const checksAll = () => {
    if (allCheckedOnPage) {
      // 現在ページのユーザーを全て外す
      const updatedList = checkedList.filter(id => !currentList.some(u => u.id === id));
      setCheckedList(updatedList);
      setCheckedUserMap(prev => {
        const newMap = {...prev};
        currentList.forEach(u => delete newMap[u.id]);
        return newMap;
      });
    } else {
      // 現在ページのユーザーを全て追加
      const updatedList = [...new Set([...checkedList, ...currentList.map(u => u.id)])];
      setCheckedList(updatedList);
      setCheckedUserMap(prev => {
        const newMap = {...prev};
        currentList.forEach(u => newMap[u.id] = u);
        return newMap;
      });
    }
  };

  // 対象者選択チェックボックス(個別)
  const checkedListAdd = (userId: string) => {
    setCheckedList(prevCheckedList => {
      if (prevCheckedList.includes(userId)) {
        // 存在する場合は削除
        setCheckedUserMap(prevMap => {
          const newMap = { ...prevMap };
          delete newMap[userId];
          return newMap;
        });
        return prevCheckedList.filter(id => id !== userId);
      } else {
        // 存在しない場合は追加
        const user = userList.find(u => u.id === userId);
        if (!user) return prevCheckedList;
        setCheckedUserMap(prevMap => ({ ...prevMap, [userId]: user }));
        return [...prevCheckedList, userId];
      }
    });
  };

  // 対象者選択用の項目
  const userCols: GridCol[] = [
    {
      field: "allChecked",
      headerName: (
        <CheckBox
          id="checksAll"
          onChange={checksAll}
          checked={allCheckedOnPage}
        />
      ),
      style: "w-1/12",
      sort: false
    },
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/6",
      sort: false
    },
    {
      field: "name",
      headerName: "名前",
      style: "w-2/6",
      sort: false
    },
    {
      field: "group",
      headerName: "所属グループ",
      style: "w-3/6",
      sort: false,
    },
  ];

  // 選択した利用者一覧の項目
  const targetUserCols: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/6",
      sort: false
    },
    {
      field: "name",
      headerName: "名前",
      style: "w-1/6",
      sort: false
    },
    {
      field: "point",
      headerName: "保有ポイント数",
      style: "w-1/6",
      sort: false
    },
    {
      field: "group",
      headerName: "所属グループ",
      style: "w-2/6",
      sort: false,
    },
  ];

  // 調整実行確認モーダルを開く
  const openConfirmModal = () => {
    try {
      if(!submitErrFlg){
        const grantPoints = Number(getValues("grantPoints"));
        const ptCategoryId = Number(getValues("ptCategoryId"));
        const ptTypeId = getValues("ptTypeId");
        const categoryName =
          pointCategoryList?.find((data) => data.categoryId === ptCategoryId)?.categoryName ?? "";
        const typeName =
          pointTypeList?.find((data) => data.typeId === ptTypeId)?.typeName ?? "";
        setInputData({
          ...inputData,
          ptCategoryId,
          ptCategoryName: categoryName,
          ptTypeId,
          ptTypeName: typeName,
          ptName: getValues("ptName"),
          grantPoints,
          occurrenceDate: new Date(getValues("occurrenceDate")),
          note: getValues("note"),
        });
        if(company){
          const checkedUserList = Object.values(checkedUserMap)
            .filter(u => checkedList.includes(u.id))
            .sort((a, b) => {
              if (a.displayId && b.displayId) {
                return a.displayId > b.displayId ? 1 : a.displayId < b.displayId ? -1 : 0;
              }
              return 0;
            });
          const targetUserRows: GridRow[] = checkedUserList.map<GridRow>((item) => {
            const pointInfo = item.possessedPointMap?.find((p) => Number(p.id) === ptCategoryId);
            const beforePoint = pointInfo?.point ?? 0;
            const afterPoint = Math.max(beforePoint + grantPoints, 0);
            return {
              rowData: {
                userId: item.displayId,
                name: item.lastName,
                beforePoint: (<div className="text-right mr-12">{beforePoint}</div>),
                afterPoint: (<div className="text-right mr-12">{afterPoint}</div>),
              },
              style: 'sm:whitespace-normal'
            }
          });
          setCheckedUserRow(targetUserRows);
        }
        setIsExecutionModalOpen(!isExecutionModalOpen);
      }
    } catch (error) {
      console.error(error);
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
    }
  };

  // ポイントタイプ選択時の処理
  const onPointTypeSelect = (ptTypeId: string) => {
    setInputData({...inputData, ptTypeId: ptTypeId});
    // ポイント設定に登録されているポイントタイプか確認
    const result = ptAwardedSettingsList.filter((item) => item.typeId === ptTypeId);
    if(result && result.length > 0){
      setValue("ptTypeFlg", false);
    }else{
      // ポイント設定がされていない
      setValue("ptTypeFlg", true);
    }
  };

  const onValidSubmit = async () => {
    if (await checkOption("point", dispatch)) {
      openConfirmModal();
    }
  }

  const userDataToGridRow = (user: UserData): GridRow => {
    const userGroupList = user.groups?.length
      ? user.groups.map(g => <Badge key={g.id} color="blue">{g.name}</Badge>)
      : null;

    // 保有ポイント表示
    const points = categoryName.map((cat) => {
      const p = user.possessedPointMap?.find(pp => Number(pp.id) === cat.id);
      const pointValue = p ? p.point : 0;
      return (
        <div key={cat.id} className="flex items-center">
          <div className="w-[7rem] text-right">{cat.name}：</div>
          <div className="w-[3rem] text-right">{pointValue}</div>
        </div>
      );
    });

    return {
      rowData: {
        allChecked: (
          <CheckBox
            id={user.id}
            class="checks"
            onChange={() => checkedListAdd(user.id)}
            checked={checkedList.includes(user.id)}
          />
        ),
        userId: user.displayId,
        name: user.lastName,
        group: userGroupList,
        point: <div className="w-[10rem] whitespace-break-spaces">{points}</div>,
      },
      style: 'sm:whitespace-normal',
    };
  };

  const checkedUserRows: GridRow[] = Object.values(checkedUserMap)
    .sort((a, b) => {
      const idA = a.displayId ?? "";
      const idB = b.displayId ?? "";
      return idA.localeCompare(idB);
    })
    .map(userDataToGridRow);

  return(
    <div className="h-full">
      <div className="items-center px-6 pt-6 pb-2">
        <h2 className="text-xl font-semibold text-gray-900">
          ポイント調整
        </h2>
      </div>
      <Progress
        subText={ConstMessage.PROGRESS_SUB_TEXT}
        open={loadProgress >= 0 && loadProgress < 100}
        progressNum={loadProgress}
        color={"green"}
      />
      {isLoad === true ? <Spiner/> : (
        <>
          <div className="">
            <div className="px-2 pt-2">
              <div className="flex items-center justify-start flex-1 flex-wrap">
                <div className="flex w-full ml-5">
                  <div className="w-[130px]">                      
                    <Button
                      className={"h-8 w-30 mt-1 pl-4 pr-4 text-sm items-center rounded-full"}
                      onClick={async () => {
                        if (await checkOption("point", dispatch)) {
                          toggle();
                          }
                        }
                      }
                      children={"対象者選択"}
                    />
                  </div>
                  {submitErrFlg ? (
                    <span role="alert" className="text-xs text-red-500 mt-3 items-center">
                      対象者を選択してください。
                    </span>
                  ) : (<span className="mt-2"></span>)}
                  {checkedList.length > 0 ? 
                  <>
                    <div className={"w-fit min-w-[800px] pr-5"}>
                      <TableList
                        gridCol={targetUserCols}
                        gridRow={checkedUserRows}
                        footer={false}
                        lineHeight={"py-2"}
                        minWidth={"max-h-[250px] overflow-auto pt-1"}
                      />
                    </div>
                  </> : <></>}
                  <TargetUserSelectedModal
                    open={isOpenTargetUserSelect}
                    toggle={toggle}
                    closeOnClickOutside
                    onClickSelect={onClickSelect} 
                    children={undefined}
                    setSelectGroup={setSelectGroup}
                    selectGroup={selectGroup}
                    setUserListResult={setUserListResult}
                    userListResult={userListResult}
                    onSelectCancel={onSelectCancel}
                    firstPageFlg={userFirstPageFlg}
                    setFirstPageFlg={setUserFirstPageFlg}
                    cols={userCols}
                    rows={userEditRows}
                    userList={userList}
                    company={company}
                    setSearchUserListFlg={setSearchUserListFlg}
                    setSearchUserList={setSearchUserList}
                    setGroupsValue={setGroupsValue}
                    groupsValue={groupsValue}
                    defaultGroups={defaultGroups}
                    onSearch={handleSearch}
                    searchCondition={searchCondition}
                    pagingFunc={pagingUserFunc}
                    totalCount={totalUserCount}
                    isLode={isUserListLoading}
                  />
                </div>
              </div>
              <form
                className=""
                onSubmit={handleSubmit(onValidSubmit)}
              >
                <div className="flex w-full h-[88px]">
                  <div className="w-[37rem] ml-5 mt-5">
                    {selectCategoryList && selectCategoryList.length > 0 ? 
                      <>
                          <RadioButton
                            children={undefined}
                            name={"ptCategoryId"}
                            label={"カテゴリ"}
                            value={selectButton}
                            radioButtonItem={selectCategoryList}
                            vertical={false}
                            blankExist= {true}
                            className={"space-y-2"}
                            required
                          />
                      </> : <></>
                    }
                  </div>
                  <div className="w-[12rem] ml-8 mt-5">
                    {selectTypeList && selectTypeList.length > 0 ? 
                      <>
                        <Controller
                          name={"ptTypeId"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <SelectFields
                              id="ptTypeId"
                              color="indigo"
                              value={inputData.ptTypeId}
                              className={"h-10 w-[192px]"}
                              selectClassName={"h-[38px]"}
                              selectItems={selectTypeList}
                              label={"タイプ"}
                              required
                              onChange={(e: any) => {onPointTypeSelect(e.target.value);field.onChange(e)}}
                              error={fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                      </> : <></>
                    }
                  </div>
                </div>
                <div className=" flex ml-5 mt-4 h-[80px]">
                  <div className="w-[37rem]">
                    <FormController
                      control={control}
                      name={"ptName"}
                      label={"ポイント名"}
                      type={"text"}
                      maxLength={40}
                      required
                    />
                  </div>
                  <div className="w-[12rem] ml-8">
                    <FormController
                      control={control}
                      className={"h-10 w-[192px]"}
                      inputClassName={'h-[38px]'}
                      name={"occurrenceDate"}
                      label={"ポイント発生日"}
                      required
                      type={"date"}
                    />
                  </div>
                  <div className="w-[10rem] ml-8">
                    <Controller
                      name={"grantPoints"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          type={"text"}
                          label={"ポイント数"}
                          required
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                          style={{ 'width': '7.5rem', 'text-align': 'right' }}
                          value={inputData.grantPoints}
                          maxLength={inputData.grantPoints && String(inputData.grantPoints).slice(0, 1) === "-" ? 6 : 5}
                          onChange={(e: any) => {
                            setInputData({...inputData, grantPoints: e.target.value});
                            setValue('grantPoints', e.target.value);
                            field.onChange(e);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex w-full">
                  <div className="w-[37rem] ml-5 min-w-[520px] mr-4 justify-start">
                    <FormController
                      control={control}
                      name={"note"}
                      label={"備考"}
                      type={"textarea"}
                      className={"h-[80px]"}
                    />
                  </div>
                  <div className="flex w-30 justify-end ml-[240px]">
                    <Button
                      className="h-8 w-30 mt-[74px] items-center text-sm px-[46px]"
                      submit
                      children ={"確定"}
                      onClick={() => checkedList.length > 0 ? "" : setSubmitErrFlg(true)}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
          {isExecutionModalOpen && (
            <ExecutionModal
              children
              open={isExecutionModalOpen}
              toggle={() => {
                setIsExecutionModalOpen(!isExecutionModalOpen);
              }}
              closeOnClickOutside={false}
              inputData={inputData}
              setLoadProgress={setLoadProgress}
              setIsExecutionModalOpen={setIsExecutionModalOpen}
              company={company}
              setIsExecution={setIsExecution}
              checkedList={checkedList}
              setExecutionErrFlg={setExecutionErrFlg}
              checkedUserRow={checkedUserRow}
            />
          )}
          {isExecutionCompleteDialogOpen && (
            <ExecutionCompleteModal
              children
              open={isExecutionCompleteDialogOpen}
              toggle={() => {
                setIsExecutionCompleteDialogOpen(!isExecutionCompleteDialogOpen);
              }}
              closeOnClickOutside={false}
              executionErrFlg={executionErrFlg}
            />
          )}
        </>
      )}
    </div>
  );
}